import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

// Reusable OTP verification overlay. Used as a gate before phone-changing
// operations (profile update) where the spec requires OTP verification (§5).
//
// Unlike the registration modal, this overlay does NOT auto-submit on
// SEND_LIMIT — there's no profile to "stash"; we just show a banner and let
// the user cancel. The caller decides what to do after onVerified fires.

const OTP_ERROR_COPY = {
  OTP_INVALID_FORMAT: 'Use international format (+1...).',
  OTP_INVALID_NUMBER: 'Invalid phone number. Please check and try again.',
  OTP_NOT_MOBILE: 'This number cannot receive SMS. Please use a mobile number.',
  OTP_REGION_BLOCKED: 'Cannot send to this region. Please use a US mobile number.',
  OTP_PHONE_TAKEN: 'This number is already in use.',
  OTP_TWILIO_ERROR: 'Failed to send. Please try again.',
  OTP_COOLDOWN: 'Too many wrong attempts. Try again in 4 hours, or use a different number.',
  OTP_SEND_LIMIT: 'Too many verification attempts for this number. Try again later.',
};

const extractOtpError = (error) => {
  const data = error?.response?.data;
  const detail = data?.detail;
  const detailObj = typeof detail === 'object' && detail !== null ? detail : null;
  const code = detailObj?.error || data?.error || null;
  const message =
    detailObj?.message ||
    (typeof detail === 'string' ? detail : null) ||
    data?.message ||
    'Failed';
  return { code, message, status: error?.response?.status };
};

const formatDisplayPhone = (phoneE164) => {
  if (!phoneE164) return '';
  const digits = phoneE164.replace(/\D/g, '');
  const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(-10);
  return formatPhoneNumber(tenDigits);
};

const OtpVerifyOverlay = ({ phoneE164, onVerified, onCancel }) => {
  const [otpCode, setOtpCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');
  const initialSendDoneRef = useRef(false);

  const sendCode = async ({ isResend = false } = {}) => {
    setSending(true);
    setError('');
    if (!isResend) setBanner('');
    try {
      const response = await authAPI.sendOTP(phoneE164);
      if (response?.success) {
        if (isResend) toast.success('Code resent');
        else toast.success('Verification code sent');
        setOtpCode('');
      }
    } catch (err) {
      const { code, message, status } = extractOtpError(err);
      logger.warn('OTP_OVERLAY', 'send failed', { code, status });
      if (status === 503 || code === 'OTP_SEND_LIMIT') {
        setBanner(OTP_ERROR_COPY.OTP_SEND_LIMIT);
        return;
      }
      if (status === 429 || code === 'OTP_COOLDOWN') {
        setBanner(OTP_ERROR_COPY.OTP_COOLDOWN);
        return;
      }
      setError((code && OTP_ERROR_COPY[code]) || message);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (initialSendDoneRef.current) return;
    initialSendDoneRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = async () => {
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const response = await authAPI.verifyOTP(phoneE164, otpCode);
      if (response?.verified) {
        onVerified();
        return;
      }
      const remaining = response?.attempts_remaining;
      if (remaining === 0) {
        // Spec §2.7 Scenario A: 4-hour cooldown, NOT admin escalation. User
        // can cancel out (= "use a different number" in settings).
        setBanner('Too many wrong attempts. Try again in 4 hours, or use a different number.');
        return;
      }
      if (remaining === null || remaining === undefined) {
        setError('Code expired — request a new one.');
      } else {
        setError(response?.message || `Invalid code. ${remaining} attempt(s) remaining.`);
      }
      setOtpCode('');
    } catch (err) {
      setError(extractOtpError(err).message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="otp-overlay-title"
    >
      <div
        className="modal-content modal-sm"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 420 }}
      >
        <div className="modal-header">
          <h3 id="otp-overlay-title">Verify Your Number</h3>
        </div>
        <div className="modal-body" style={{ padding: '16px 20px' }}>
          <p style={{ fontSize: '14px', marginBottom: '14px' }}>
            We sent a 6-digit code to {formatDisplayPhone(phoneE164)}.
          </p>

          {banner && (
            <div
              role="alert"
              style={{
                padding: '10px 12px',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                marginBottom: '12px',
                fontSize: '13px',
                color: '#92400e',
              }}
            >
              {banner}
            </div>
          )}

          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="------"
            aria-label="6-digit verification code"
            style={{
              width: '200px',
              letterSpacing: '10px',
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 600,
              padding: '12px',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
            }}
            autoFocus
            disabled={!!banner}
          />

          {error && (
            <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <button
              type="button"
              onClick={() => sendCode({ isResend: true })}
              disabled={sending || !!banner}
              style={{
                background: 'none',
                border: 'none',
                color: (!!banner) ? '#9ca3af' : '#3b82f6',
                cursor: (!!banner) ? 'not-allowed' : 'pointer',
                padding: 0,
                fontSize: '13px',
                textAlign: 'left',
              }}
            >
              {sending ? 'Sending...' : "Didn't get it? Resend code"}
            </button>
            {/* Always available: a wrong phone number must be fixable immediately. */}
            <button
              type="button"
              onClick={onCancel}
              disabled={verifying}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                cursor: verifying ? 'not-allowed' : 'pointer',
                padding: 0,
                fontSize: '13px',
                textAlign: 'left',
              }}
            >
              Use a different number
            </button>
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-outline" onClick={onCancel} disabled={verifying}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleVerify}
            disabled={verifying || otpCode.length < 6 || !!banner}
          >
            {verifying ? 'Verifying...' : 'Verify & Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtpVerifyOverlay;
